import { Sparkles } from "lucide-react";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { LegalRedirectDrawer, PasswordInput } from "@/components/common";
import { Button, Checkbox, Input } from "@/components/ui";
import { ROUTES } from "@/constants/routes";
import { gtmService } from "@/services/gtm-service";

import { GoogleAuthButton } from "./components/GoogleAuthButton";
import { useAuthRedirect } from "./hooks/useAuthRedirect";
import { useSignUp } from "./hooks/useSignUp";

export const SignUpForm = () => {
  const { t } = useTranslation("Quiz", { keyPrefix: "QuizSignUpForm" });
  const location = useLocation();
  const { redirectAfterAuth } = useAuthRedirect();

  const onSuccess = () => {
    redirectAfterAuth();
  };

  const {
    email,
    setEmail,
    password,
    setPassword,
    terms,
    setTerms,
    acceptMarketing,
    setAcceptMarketing,
    handleSubmit,
    isLoading,
    isValid,
    error,
  } = useSignUp({ onSuccess });

  useEffect(() => {
    gtmService.trackSignUpView();
  }, []);

  return (
    <div className="w-full max-w-md">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary shadow-lg shadow-primary/25">
          <Sparkles className="h-7 w-7 text-white" />
        </div>
        <h1 className="font-bold text-3xl text-zinc-900">Create account</h1>
        <p className="mt-2 text-zinc-500">Get started with your AI twin</p>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-destructive bg-destructive/10 px-4 py-3 text-destructive text-sm">
          {error}
        </div>
      )}

      <GoogleAuthButton
        mode="signup"
        onSuccess={onSuccess}
        isDisabled={isLoading || !terms}
        acceptMarketing={acceptMarketing}
      />

      <div className="my-5 flex items-center gap-3">
        <hr className="flex-1 border-zinc-200" />
        <span className="text-xs text-zinc-400">or</span>
        <hr className="flex-1 border-zinc-200" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-1.5">
          <label className="block font-medium text-sm text-zinc-700">Email</label>
          <Input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="space-y-1.5">
          <label className="block font-medium text-sm text-zinc-700">Password</label>
          <PasswordInput
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={terms}
            onCheckedChange={(checked) => setTerms(checked === true)}
            className="size-6"
          />
          <p className="font-medium text-muted-foreground text-sm">
            <Trans
              t={t}
              i18nKey="termsAgreement"
              components={{
                terms: <LegalRedirectDrawer type="terms" />,
                privacy: <LegalRedirectDrawer type="privacy" />,
              }}
            />
          </p>
        </label>

        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={acceptMarketing}
            onCheckedChange={(checked) => setAcceptMarketing(checked === true)}
            className="size-6"
          />
          <p className="font-medium text-muted-foreground text-sm">
            {t("marketingAgreement")}
          </p>
        </label>

        <Button
          type="submit"
          disabled={isLoading || !isValid || !terms}
          className="h-12 w-full text-base"
        >
          {isLoading ? "Creating account..." : "Let's go"}
        </Button>

        <div className="text-center">
          <span className="text-sm text-zinc-500">Already have an account? </span>
          <Link
            to={ROUTES.LOGIN}
            state={location.state}
            className="font-semibold text-primary text-sm no-underline hover:text-primary-hover"
          >
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
};
